import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useShopFlag } from "@/hooks/use-flag";
import { SizingRecommendationBadge } from "@/pages/(app)/analytics/sizing/components/sizing-recommendation-badge";
import {
  MOCK_DIAGNOSTIC,
  USE_MOCK_DATA,
} from "@/pages/(app)/analytics/sizing/lib/mock-data";
import useSizingDiagnostic from "@babackapp/private-api-client/client/use-sizing-diagnostic";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useDashboardFilters } from "../lib/dashboard-filters";
import useFormattedTimeRange from "../lib/use-formatted-time-range";

export function SizingDiagnosticList() {
  const { data: shop } = useCurrentShopSuspense();
  const { toTimestamp, fromTimestamp } = useDashboardFilters();
  const t = useTranslation();
  const timeRange = useFormattedTimeRange();
  const isSizingEnabled = useShopFlag("isSizingEnabled");

  const apiResult = useSizingDiagnostic({
    shopId: shop.id,
    fromTimestamp,
    toTimestamp,
    limit: 5,
  });

  const diagnosticItems = USE_MOCK_DATA
    ? MOCK_DIAGNOSTIC.slice(0, 5)
    : apiResult.data;

  if (!isSizingEnabled) return null;
  if (!diagnosticItems || diagnosticItems.length === 0) return null;

  return (
    <Card className="col-span-2">
      <CardHeader className="pb-0">
        <CardTitle className="text-md">
          {t("sizing.diagnostic.title")}
        </CardTitle>
        <CardDescription>{timeRange}</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-96 mt-4">
          <div className="flex flex-col gap-2">
            {diagnosticItems.map((item) => (
              <div
                key={item.productId}
                className="flex justify-between items-center bg-muted/50 rounded-lg p-4"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.productTitle}
                      className="h-10 w-10 rounded-md object-cover flex-shrink-0"
                    />
                  )}
                  <span className="text-sm font-semibold text-muted-foreground truncate">
                    {item.productTitle}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <SizingRecommendationBadge
                    recommendation={item.recommendation}
                  />
                  {item.ratio != null && (
                    <Badge variant="outline">
                      {(item.ratio * 100).toFixed(1)}%
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <Link
          to={`/shops/${shop.id}/analytics/sizing`}
          className="flex items-center gap-1 text-sm text-primary hover:underline mt-4"
        >
          {t("sizing.diagnostic.viewAll")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}
